import { type AppSettings, type BlockedNumberItem, type CommDirectory, type ContactSyncStatus, formatPhone, toE164 } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { del, errorMessage, get, post } from "../../api";
import { Alert, Button, Checkbox, Field, Loading, PageHeader, Panel, PanelHeader, formatDateTime } from "../../components/ui";
import { useCommStatus } from "../Comms";
import { useSettings } from "./useSettings";

type Ext = AppSettings["comms.extensions"][number];

/** Settings → Texts and calls: copying from RingCentral, which lines the CRM watches, and what texting can do here. */
export function CommsSettings() {
  const qc = useQueryClient();
  const status = useCommStatus();
  const { query, save } = useSettings();
  const check = useMutation({ mutationFn: () => post<{ added: number }>("/api/comms/sync"), onSettled: () => qc.invalidateQueries({ queryKey: ["comms-status"] }) });
  if (status.isLoading || query.isLoading) return <Loading />;
  const s = status.data!;
  const settings = query.data!.settings;
  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Texts and calls" />
      <Panel>
        <PanelHeader title="RingCentral" description={s.connected ? `Connected. Texts go out from ${formatPhone(s.fromNumber)}.` : "RingCentral isn't set up on this server."} />
        <div className="flex flex-col gap-4 px-5 py-4 text-sm">
          {save.error && <Alert>{errorMessage(save.error)}</Alert>}
          <Checkbox
            label="Copy texts, calls, and voicemails from RingCentral"
            description="Gets texts and ringing calls the moment they happen, checks right after each call for its log and any voicemail, and does a full check every 30 minutes for anything missed. Only NMT's numbers are kept: the RingCentral account also has another business's numbers, and their messages are never stored."
            checked={!!settings["comms.syncEnabled"]}
            disabled={!s.connected || save.isPending}
            onChange={(v) => save.mutate({ "comms.syncEnabled": v }, { onSuccess: () => qc.invalidateQueries({ queryKey: ["comms-status"] }) })}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={!s.connected} busy={check.isPending} onClick={() => check.mutate()}>
              Check now
            </Button>
            <span className="text-muted">{s.lastSyncAt ? `Last checked ${formatDateTime(s.lastSyncAt)}` : "Not checked yet"}</span>
            {check.data && <span>{check.data.added} new</span>}
          </div>
          {(check.error || s.lastSyncError) && <Alert>{check.error ? errorMessage(check.error) : s.lastSyncError}</Alert>}
          <div className="rounded-[var(--radius-control)] bg-mist/60 px-4 py-3">
            <div className="font-semibold">Live events (incoming-call pop-up)</div>
            {s.live.error ? (
              <p className="text-brand">{s.live.error}</p>
            ) : s.live.active ? (
              <p>On{s.live.expiresAt ? `, renewed automatically (good until ${formatDateTime(s.live.expiresAt)})` : ""}.</p>
            ) : (
              <p>Off.{s.live.note ? ` ${s.live.note}` : ""}</p>
            )}
            {s.live.active && s.live.note && <p>{s.live.note}</p>}
          </div>
          <div className="rounded-[var(--radius-control)] bg-mist/60 px-4 py-3">
            <div className="font-semibold">Sending texts</div>
            {s.redirectTo ? (
              <p>Testing: every text the CRM sends (replies, reminders, payment links, automations) goes to the test phone, {formatPhone(s.redirectTo)}, labeled with who it was for. It's filed under the real person. Someone who replied STOP still isn't texted. This is a server setting and can't be on in production.</p>
            ) : s.testNumbers === null ? (
              <p>Texts go to anyone (live).</p>
            ) : s.testNumbers.length ? (
              <p>Until go-live, texts only go to these test numbers: {s.testNumbers.map(formatPhone).join(", ")}. The list is part of the server setup, not a setting, so nobody can text a customer by accident.</p>
            ) : (
              <p>Until go-live, texts only go to test numbers, and none are set up on this server yet, so texting is off.</p>
            )}
          </div>
        </div>
      </Panel>
      <Extensions connected={s.connected} saved={settings["comms.extensions"]} />
      <Numbers fromNumber={s.fromNumber} saved={settings["comms.numbers"]} />
      <ContactSync connected={s.connected} />
      <Blocked />
    </div>
  );
}

/** Numbers that don't pop up, aren't texted by automations, and are blocked in RingCentral (production). */
function Blocked() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["comms-blocked"], queryFn: () => get<BlockedNumberItem[]>("/api/comms/blocked") });
  const [number, setNumber] = useState("");
  const [label, setLabel] = useState("");
  const done = () => {
    void qc.invalidateQueries({ queryKey: ["comms-blocked"] });
    void qc.invalidateQueries({ queryKey: ["comm-threads"] });
  };
  const add = useMutation({
    mutationFn: () => post("/api/comms/blocked", { number, label }),
    onSuccess: () => {
      setNumber("");
      setLabel("");
      done();
    },
  });
  const remove = useMutation({ mutationFn: (n: string) => del(`/api/comms/blocked/${encodeURIComponent(n)}`), onSuccess: done });
  const retry = useMutation({ mutationFn: (n: string) => post(`/api/comms/blocked/${encodeURIComponent(n)}/ringcentral`), onSettled: done });
  const err = add.error ?? remove.error ?? retry.error;
  return (
    <Panel>
      <PanelHeader title="Blocked numbers" description="Calls from these numbers don't pop up, automations never text them, and (from production) they're blocked in RingCentral on each line the CRM watches. Block from a call pop-up or a conversation, or add one here." />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {err && <Alert>{errorMessage(err)}</Alert>}
        {list.isLoading ? (
          <Loading />
        ) : !list.data?.length ? (
          <p className="text-muted">No blocked numbers.</p>
        ) : (
          <ul className="divide-y divide-mist">
            {list.data.map((b) => {
              const failed = b.ringcentral?.filter((r) => !r.ok) ?? [];
              return (
                <li key={b.number} className="flex flex-col gap-1 py-2">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-medium">{formatPhone(b.number)}</span>
                    <span>{b.label}</span>
                    <span className="text-xs text-muted">
                      Blocked {formatDateTime(b.blockedAt)}
                      {b.blockedBy ? ` by ${b.blockedBy}` : ""}
                    </span>
                    <Button className="ml-auto px-3 py-1" busy={remove.isPending && remove.variables === b.number} onClick={() => remove.mutate(b.number)}>
                      Unblock
                    </Button>
                  </div>
                  <div className="text-xs text-muted">
                    {b.ringcentral === null
                      ? "RingCentral: changed from production only."
                      : !b.ringcentral.length
                        ? "RingCentral: no lines picked to watch (above), so not blocked there."
                        : `RingCentral: ${b.ringcentral.filter((r) => r.ok).map((r) => r.line).join(", ") || "not blocked on any line"}${b.ringcentral.some((r) => r.ok) ? " ✓" : ""}`}
                  </div>
                  {failed.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-danger">
                      {failed.map((f) => `${f.line}: ${f.error ?? "not blocked"}`).join(" ")}
                      <Button variant="quiet" className="px-2 py-0.5 text-xs" busy={retry.isPending && retry.variables === b.number} onClick={() => retry.mutate(b.number)}>
                        Try again
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Field label="Number" type="tel" value={number} onChange={(e) => setNumber(e.target.value)} />
          <Field label="Why" placeholder="Robocaller" value={label} maxLength={200} onChange={(e) => setLabel(e.target.value)} />
          <Button type="submit" busy={add.isPending} disabled={number.replace(/\D/g, "").length < 10 || !label.trim()}>
            Block
          </Button>
        </form>
      </div>
    </Panel>
  );
}

/** Which RingCentral extensions the CRM watches. */
function Extensions({ connected, saved }: { connected: boolean; saved: Ext[] }) {
  const qc = useQueryClient();
  const { save } = useSettings();
  const [picked, setPicked] = useState<Ext[]>(saved);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setPicked(saved), [saved]);
  const dir = useQuery({ queryKey: ["comms-directory"], queryFn: () => get<CommDirectory>("/api/comms/directory"), enabled: connected, retry: false });
  const numbersOf = (id: string) => (dir.data?.numbers ?? []).filter((n) => n.extensionId === id).map((n) => formatPhone(n.number));
  const list: Ext[] = dir.data ? dir.data.extensions.map((e) => ({ id: e.id, number: e.number, name: e.name })) : saved;
  const typeOf = (id: string) => dir.data?.extensions.find((e) => e.id === id)?.type ?? "";
  const on = (id: string) => picked.some((p) => p.id === id);
  const changed = JSON.stringify([...picked].sort((a, b) => a.id.localeCompare(b.id))) !== JSON.stringify([...saved].sort((a, b) => a.id.localeCompare(b.id)));
  return (
    <Panel>
      <PanelHeader
        title="Extensions"
        description="The lines the CRM watches: each person's extension and the main line's call queue (and the fax). Their texts, calls, and voicemails are copied. Calls ringing an extension pop up for the person on it (pick it on their user page under Settings → Users). People see only their own extension's unless their role has 'See all lines'."
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {dir.error && <Alert>{errorMessage(dir.error)}</Alert>}
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        {dir.isLoading && <Loading />}
        {!list.length && !dir.isLoading && <p className="text-muted">No extensions to pick from yet.</p>}
        <ul className="flex flex-col gap-1">
          {list.map((e) => (
            <li key={e.id}>
              <Checkbox
                label={`${e.number} · ${e.name}${typeOf(e.id) && typeOf(e.id) !== "User" ? ` (${typeOf(e.id) === "Department" ? "call queue" : typeOf(e.id)})` : ""}`}
                description={numbersOf(e.id).join(", ") || undefined}
                checked={on(e.id)}
                onChange={(v) => setPicked(v ? [...picked, e] : picked.filter((p) => p.id !== e.id))}
              />
            </li>
          ))}
        </ul>
        <div>
          <Button
            variant="primary"
            disabled={!changed}
            busy={save.isPending}
            onClick={() =>
              save.mutate(
                { "comms.extensions": picked },
                {
                  onSuccess: async () => {
                    setNotice("Saved. Press Check now to bring in their texts and calls and start the pop-up on them.");
                    await qc.invalidateQueries({ queryKey: ["comms-status"] });
                  },
                },
              )
            }
          >
            Save extensions
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** NMT's numbers besides the main one: only texts and calls on NMT's numbers are kept. */
function Numbers({ fromNumber, saved }: { fromNumber: string | null; saved: string[] }) {
  const { save } = useSettings();
  const [text, setText] = useState(saved.map(formatPhone).join(", "));
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => setText(saved.map(formatPhone).join(", ")), [saved]);
  const submit = () => {
    setProblem(null);
    setNotice(null);
    const parts = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
    const bad = parts.find((p) => !toE164(p));
    if (bad) return setProblem(`"${bad}" isn't a US phone number.`);
    save.mutate({ "comms.numbers": [...new Set(parts.map((p) => toE164(p)!))] }, { onSuccess: () => setNotice("Saved.") });
  };
  return (
    <Panel>
      <PanelHeader
        title="NMT's numbers"
        description={`Texts, calls, and voicemails are only kept when they're on one of NMT's numbers${fromNumber ? ` (${formatPhone(fromNumber)}, the main line, always is)` : ""}. Add the fax and each person's direct line here. Anything on another business's number is dropped.`}
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {(problem || save.error) && <Alert>{problem ?? errorMessage(save.error)}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        <Field label="Other numbers (comma between)" value={text} onChange={(e) => setText(e.target.value)} placeholder="405-217-0667, 405-253-5777" />
        <div>
          <Button variant="primary" busy={save.isPending} onClick={submit}>
            Save numbers
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** Contacts copied into each person's RingCentral address book (one way). */
function ContactSync({ connected }: { connected: boolean }) {
  const qc = useQueryClient();
  const { save } = useSettings();
  const st = useQuery({ queryKey: ["contact-sync"], queryFn: () => get<ContactSyncStatus>("/api/comms/contact-sync"), refetchInterval: 60_000 });
  const run = useMutation({ mutationFn: () => post<ContactSyncStatus>("/api/comms/contact-sync"), onSuccess: (d) => qc.setQueryData(["contact-sync"], d) });
  const d = st.data;
  return (
    <Panel>
      <PanelHeader
        title="Contacts in RingCentral"
        description="Copies each contact's name and phone numbers into the RingCentral address book of everyone with an extension, so the desk phone and the RingCentral app show who's calling. One way: change contacts in the CRM; changes made in RingCentral are replaced. Turning it off removes the copies."
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {(save.error || run.error) && <Alert>{errorMessage(save.error ?? run.error)}</Alert>}
        {d?.lastError && <Alert>Last copy failed: {d.lastError}</Alert>}
        <Checkbox
          label="Copy contacts into RingCentral"
          description="About 30 changes every 2 minutes (RingCentral limits how fast), so the first copy of a few hundred contacts takes a while."
          checked={!!d?.enabled}
          disabled={!connected || save.isPending || !d}
          onChange={(v) => save.mutate({ "comms.contactSync": v }, { onSuccess: () => qc.invalidateQueries({ queryKey: ["contact-sync"] }) })}
        />
        {d && (
          <p className="text-muted">
            {d.contacts} contacts with a phone number.{" "}
            {d.extensions.length ? d.extensions.map((e) => `${e.name || e.id}: ${e.copied} copied`).join(" · ") : "Nobody has an extension yet (Settings → Users)."}
            {d.lastRunAt ? ` Last copied ${formatDateTime(d.lastRunAt)}.` : ""}
          </p>
        )}
        <div>
          <Button disabled={!connected || !d?.enabled} busy={run.isPending} onClick={() => run.mutate()}>
            Copy now
          </Button>
        </div>
      </div>
    </Panel>
  );
}
